import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaSesSettings,
  SaSesSettings,
} from "../../../helpers/super_admin_ops_helper";

/** Passos do guia BYO — espelham ops_checklist no binding do tenant. */
export const BYO_GUIDE_STEPS = [
  "deal",
  "createTenant",
  "enableMode",
  "awsAccount",
  "iam",
  "accessKey",
  "saveKeysSa",
  "configSet",
  "snsTopic",
  "eventDest",
  "httpsSub",
  "confirmSub",
  "domainDns",
  "tracking",
  "smokeTest",
  "activate",
] as const;

export type ByoGuideStep = (typeof BYO_GUIDE_STEPS)[number];

const PHASES: { id: string; steps: ByoGuideStep[] }[] = [
  { id: "commercial", steps: ["deal", "createTenant", "enableMode"] },
  {
    id: "aws",
    steps: [
      "awsAccount",
      "iam",
      "accessKey",
      "saveKeysSa",
      "configSet",
      "snsTopic",
      "eventDest",
      "httpsSub",
      "confirmSub",
    ],
  },
  { id: "product", steps: ["domainDns", "tracking", "smokeTest", "activate"] },
];

/**
 * Runbook passo a passo: conta AWS do cliente (BYO SES).
 * Só Super Admin — espelha DOC 14 § 8.4 / 02 § 5.5.
 */
const SuperAdminSesByoGuide = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId");
  const tenantId =
    tenantIdParam && Number.isFinite(Number(tenantIdParam))
      ? Number(tenantIdParam)
      : null;

  const [settings, setSettings] = useState<SaSesSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  document.title = t("sa.sesByo.guide.documentTitle");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getSaSesSettings();
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(t("sa.ses.error.load"));
          return;
        }
        setSettings(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || t("sa.ses.error.load"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const webhookUrl = useMemo(
    () => settings?.webhook_url || "",
    [settings?.webhook_url]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.sesByo.guide.title")}
          pageTitle={t("sa.menu.ops")}
        />
        <ToastContainer closeButton={false} limit={2} />

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            color="soft-secondary"
            size="sm"
            tag={Link}
            to="/super-admin/ses/setup-guide"
          >
            {t("sa.sesByo.guide.linkPlatformGuide")}
          </Button>
          <Button
            color="soft-primary"
            size="sm"
            tag={Link}
            to="/super-admin/ses/endpoints"
          >
            {t("sa.ses.link.endpoints")}
          </Button>
          {tenantId !== null && (
            <Button
              color="primary"
              size="sm"
              tag={Link}
              to={`/super-admin/tenants/${tenantId}`}
            >
              {t("sa.sesByo.guide.backToTenant")}
            </Button>
          )}
        </div>

        <Alert color="warning" className="mb-3">
          {t("sa.sesByo.guide.notSelfServe")}
        </Alert>

        {error && <Alert color="danger">{error}</Alert>}

        <Row>
          <Col lg={8}>
            <Card className="mb-3">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("sa.sesByo.guide.introTitle")}
                </h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted mb-2">{t("sa.sesByo.guide.intro")}</p>
                <p className="mb-0 small">{t("sa.sesByo.guide.introHint")}</p>
              </CardBody>
            </Card>

            {PHASES.map((phase) => (
              <Card key={phase.id} className="mb-3">
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">
                    {t(`sa.sesByo.guide.phases.${phase.id}.title`)}
                  </h5>
                  <Badge color="secondary" pill>
                    {phase.steps.length}
                  </Badge>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small">
                    {t(`sa.sesByo.guide.phases.${phase.id}.blurb`)}
                  </p>
                  <ol className="mb-0 ps-3">
                    {phase.steps.map((step) => (
                      <li key={step} className="mb-3">
                        <strong>
                          {t(`sa.sesByo.guide.steps.${step}.title`)}
                        </strong>
                        <p className="text-muted mb-1 mt-1">
                          {t(`sa.sesByo.guide.steps.${step}.body`)}
                        </p>
                        {step === "httpsSub" && webhookUrl !== "" && (
                          <FormGroup className="mb-0">
                            <Label
                              for="byo-webhook"
                              className="form-label small mb-1"
                            >
                              {t("sa.ses.settings.webhookUrl")}
                            </Label>
                            <Input
                              id="byo-webhook"
                              type="text"
                              readOnly
                              value={webhookUrl}
                              onFocus={(e) => e.target.select()}
                            />
                          </FormGroup>
                        )}
                        {step === "enableMode" && tenantId !== null && (
                          <Button
                            color="soft-primary"
                            size="sm"
                            tag={Link}
                            to={`/super-admin/tenants/${tenantId}#ses-byo`}
                          >
                            {t("sa.sesByo.guide.openTenantBinding")}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ol>
                </CardBody>
              </Card>
            ))}
          </Col>

          <Col lg={4}>
            <Card className="mb-3">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("sa.ses.settings.webhookUrl")}
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : (
                  <>
                    <Input
                      type="text"
                      readOnly
                      value={webhookUrl}
                      onFocus={(e) => e.target.select()}
                    />
                    <p className="text-muted small mb-0 mt-2">
                      {t("sa.sesByo.guide.webhookHint")}
                    </p>
                  </>
                )}
              </CardBody>
            </Card>

            <Alert color="info" className="mb-3">
              {t("sa.sesByo.guide.iamNote")}
            </Alert>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("sa.sesByo.guide.checklistTitle")}
                </h5>
              </CardHeader>
              <CardBody>
                <p className="text-muted small">
                  {t("sa.sesByo.guide.checklistHint")}
                </p>
                <ul className="mb-0 ps-3 small">
                  {BYO_GUIDE_STEPS.map((step) => (
                    <li key={step} className="mb-1">
                      {t(`sa.sesByo.guide.steps.${step}.title`)}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperAdminSesByoGuide;
